import React from 'react'

import { cn } from '@/utils/common'

export interface SectionHeaderProps {
  className?: string
  children: React.ReactNode
}

export const SectionHeader: React.FC<SectionHeaderProps> = ({
  className,
  children,
}) => {
  return (
    <h6
      className={cn(
        'uppercase',
        'font-bold',
        'text-[var(--Colors-Text-text-quaternary-500)]',
        'm-0',
        className
      )}
      style={{
        fontSize: 'var(--Font-size-text-xs)',
        lineHeight: 'var(--Line-height-text-xs)',
      }}
    >
      {children}
    </h6>
  )
}

export interface StyledListProps {
  className?: string
  children: React.ReactNode
}

export const StyledList: React.FC<StyledListProps> = ({
  className,
  children,
}) => {
  return (
    <ul
      className={cn(
        'list-none',
        '[&>:last-child]:pb-0',
        'group [&>li>button]:!pl-10',
        className
      )}
      style={{ padding: '0', margin: '0' }}
    >
      {children}
    </ul>
  )
}

export interface StyledListItemProps {
  className?: string
  children: React.ReactNode
}

export const StyledListItem: React.FC<StyledListItemProps> = ({
  className,
  children,
}) => {
  return (
    <li
      className={cn('py-0', className)}
      style={{ padding: 'var(--spacing-xxs) 0' }}
    >
      {children}
    </li>
  )
}

export interface StyledListItemButtonProps {
  className?: string
  selected?: boolean
  onClick?: () => void
  children: React.ReactNode
}

export const StyledListItemButton: React.FC<StyledListItemButtonProps> = ({
  className,
  selected = false,
  onClick,
  children,
}) => {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full border-none bg-transparent flex items-center text-left',
        'cursor-pointer',
        'transition-colors duration-200',
        'hover:bg-[var(--Colors-Background-bg-primary)]',
        selected && [
          'bg-[var(--Colors-Background-bg-primary)]',
          'font-semibold',
          '[&_.sidebar-item-text]:font-semibold',
        ],
        className
      )}
      style={{
        padding: 'var(--spacing-lg) var(--spacing-md)',
        gap: 'var(--spacing-lg)',
        borderRadius: 'var(--radius-sm)',
        color: selected ? 'var(--Colors-Text-text-secondary_hover)' : undefined,
      }}
    >
      {children}
    </button>
  )
}

export interface StyledListItemIconProps {
  className?: string
  children: React.ReactNode
}

export const StyledListItemIcon: React.FC<StyledListItemIconProps> = ({
  className,
  children,
}) => {
  return (
    <span className={cn('min-w-0 flex-shrink-0 inline-flex', className)}>
      {children}
    </span>
  )
}

export interface StyledListItemTextProps {
  className?: string
  primary?: React.ReactNode
  children?: React.ReactNode
}

export const StyledListItemText: React.FC<StyledListItemTextProps> = ({
  className,
  primary,
  children,
}) => {
  const content = primary || children

  return (
    <span
      className={cn('m-0 flex-1 sidebar-item-text', className)}
      style={{
        fontSize: 'var(--Font-size-text-sm)',
        fontWeight: 'inherit',
        lineHeight: 'var(--Line-height-text-sm)',
        color: 'var(--Colors-Text-text-secondary-700)',
      }}
    >
      {content}
    </span>
  )
}

export interface StyledGroupHeaderProps {
  className?: string
  id?: string
  'aria-expanded'?: boolean
  onClick?: () => void
  children: React.ReactNode
}

export const StyledGroupHeader: React.FC<StyledGroupHeaderProps> = ({
  className,
  id,
  'aria-expanded': ariaExpanded,
  onClick,
  children,
}) => {
  return (
    <button
      id={id}
      type="button"
      aria-expanded={ariaExpanded}
      onClick={onClick}
      className={cn(
        'w-full border-none bg-transparent',
        'flex items-center cursor-pointer',
        'transition-colors duration-200',
        'hover:bg-[var(--Colors-Background-bg-primary)]',
        className
      )}
      style={{
        padding: 'var(--spacing-lg) var(--spacing-md)',
        borderRadius: 'var(--radius-sm)',
        gap: 'var(--spacing-lg)',
        flex: '1 0 0',
        color: 'var(--Colors-Text-text-secondary-700)',
      }}
    >
      {children}
    </button>
  )
}
